import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, ToggleButton, ToggleButtonGroup,
  Typography, Box, IconButton, Chip, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const EMPTY = { type: 'bug', name: '', email: '', subject: '', message: '' };

export default function FeedbackDialog({ open, onClose }) {
  const [form, setForm]         = useState(EMPTY);
  const [images, setImages]     = useState([]);   // array of File
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]     = useState(null); // { ok } | { error }
  const fileRef = useRef();

  const reset = () => { setForm(EMPTY); setImages([]); setResult(null); };

  const handleClose = () => { reset(); onClose(); };

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    setImages(prev => {
      const combined = [...prev, ...incoming];
      return combined.slice(0, 5); // max 5
    });
    e.target.value = '';
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const isValid = form.name.trim() && form.email.trim() && form.subject.trim() && form.message.trim();

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setResult(null);
    try {
      const body = new FormData();
      body.append('type',    form.type);
      body.append('name',    form.name.trim());
      body.append('email',   form.email.trim());
      body.append('subject', form.subject.trim());
      body.append('message', form.message.trim());
      images.forEach(f => body.append('images', f));

      const r = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        body,
        credentials: 'include',
      });
      if (r.ok) {
        setResult({ ok: true });
        setForm(EMPTY);
        setImages([]);
      } else {
        const j = await r.json().catch(() => ({}));
        setResult({ error: j.message || `Server error ${r.status}` });
      }
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Submit Bug or Feature Request
        <IconButton onClick={handleClose} size="small"
          sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {result?.ok ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your feedback has been sent. Thank you!
          </Alert>
        ) : result?.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{result.error}</Alert>
        ) : null}

        {/* Type selector */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Type
        </Typography>
        <ToggleButtonGroup
          value={form.type}
          exclusive
          onChange={(_, v) => { if (v) setForm(f => ({ ...f, type: v })); }}
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="bug" sx={{ gap: 0.5 }}>
            <BugReportIcon fontSize="small" /> Bug Report
          </ToggleButton>
          <ToggleButton value="feature" sx={{ gap: 0.5 }}>
            <LightbulbIcon fontSize="small" /> Feature Request
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Name + Email row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Your Name"
            size="small"
            sx={{ flex: 1 }}
            required
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Your Email"
            size="small"
            type="email"
            sx={{ flex: 1 }}
            required
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
        </Box>

        {/* Subject */}
        <TextField
          label="Subject"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          value={form.subject}
          onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
        />

        {/* Message */}
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={5}
          size="small"
          sx={{ mb: 2 }}
          value={form.message}
          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
        />

        {/* Image attachments */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => fileRef.current?.click()}
            disabled={images.length >= 5}
          >
            Attach Images {images.length > 0 ? `(${images.length}/5)` : ''}
          </Button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFiles}
          />
          {images.map((f, i) => (
            <Chip
              key={i}
              label={f.name}
              size="small"
              onDelete={() => removeImage(i)}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Up to 5 images, 10 MB each
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !isValid}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
